import React, { useState, type FC, useEffect } from 'react';
import { DatePicker } from '@atlaskit/datetime-picker';
import InlineEdit from '@atlaskit/inline-edit';
import { Box, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useLocale } from '@atlassian/jira-tenant-context-controller';

import commonMessages from '../messages';
import { InlineEditContainer } from '../styled';
import type { Field } from '../types';
import messages from '../value-container/messages';
import { DateFieldReadView } from './read-view';

// value format: YYYY-MM-DD
export const DateField: FC<Field> = ({ id, label, value, onChange, getValueContainer }) => {
	const { formatDate, formatMessage } = useIntl();
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [currentValue, setCurrentValue] = useState(value || '');
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const locale = useLocale();
	const valueForAriaLabel = value || formatMessage(commonMessages.noValue);

	useEffect(() => {
		setCurrentValue(() => value || '');
	}, [value]);

	const onChangeWithAnalytics = (newValue: string) => {
		if (newValue === value) return;
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dateFieldType',
		});
		onChange({ value: newValue }, analyticsEvent);
	};

	return (
		<InlineEditContainer>
			<InlineEdit
				defaultValue={undefined}
				editButtonLabel={formatMessage(commonMessages.editButtonLabel, {
					label,
					value: valueForAriaLabel,
				})}
				editView={({ errorMessage, ...fieldProps }) => (
					<Box xcss={containerStyles}>
						<DatePicker
							{...fieldProps}
							id={id}
							value={currentValue}
							locale={locale}
							onChange={(newValue) => {
								setCurrentValue(() => newValue);
								onChangeWithAnalytics(newValue);
							}}
							spacing="compact"
							isOpen={isEditing}
							formatDisplayLabel={(v: string, _: string) => formatDate(v)}
							onBlur={() => setIsEditing(false)}
							autoFocus
							placeholder={formatMessage(messages.none)}
						/>
					</Box>
				)}
				readView={() => (
					<DateFieldReadView value={value} getValueContainer={getValueContainer} inInlineEdit />
				)}
				readViewFitContainerWidth
				isEditing={isEditing}
				onEdit={() => setIsEditing(true)}
				hideActionButtons
				onConfirm={() => setIsEditing(false)}
			/>
		</InlineEditContainer>
	);
};

const containerStyles = xcss({
	marginLeft: 'space.negative.025',
});

export { DateFieldReadView };
